import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SEOHead from '../common/SEOHead';
import SafeIcon from '../common/SafeIcon';

const { FiMapPin, FiHome, FiUsers, FiTool, FiDollarSign, FiShield, FiCheck, FiStar } = FiIcons;

const ServiceAreasPage = () => {
  const areas = [
    {
      name: 'Calgary',
      description: 'Comprehensive property management services across all Calgary quadrants including NW, NE, SW, and SE communities.',
      neighborhoods: [
        'Downtown', 'Beltline', 'Mission', 'Inglewood', 'Bridgeland', 'Hillhurst', 'Mount Pleasant', 
        'Sunnyside', 'Bowness', 'Tuscany', 'Panorama Hills', 'Sage Hill', 'Skyview Ranch'
      ],
      stats: { properties: '300+', satisfaction: '98%' }
    },
    {
      name: 'Airdrie',
      description: 'Specialized property management services for Airdrie\'s growing residential and commercial markets.',
      neighborhoods: [
        'Countryside', 'Ravenswood', 'East Lake', 'Westmere', 'North Ridge', 'South Pointe', 'Silvertip'
      ],
      stats: { properties: '100+', satisfaction: '97%' }
    },
    {
      name: 'Edmonton',
      description: 'Professional property management services for Edmonton\'s diverse neighborhoods and growing rental market.',
      neighborhoods: [
        'Downtown', 'Riverbend', 'Mill Woods', 'Sherwood Park', 'St. Albert', 'Spruce Grove', 
        'West Edmonton', 'South Edmonton', 'North Edmonton'
      ],
      stats: { properties: '80+', satisfaction: '96%' }
    }
  ];

  const services = [
    {
      icon: FiHome,
      title: 'Property Management',
      description: 'Complete property management solutions including tenant placement, maintenance, and financial reporting.',
      link: '/services/property-management'
    },
    {
      icon: FiUsers,
      title: 'Tenant Placement',
      description: 'Professional tenant screening and placement with 90% reduction in evictions.',
      link: '/services/tenant-placement'
    },
    {
      icon: FiTool,
      title: 'Maintenance Services',
      description: '24/7 emergency repairs and preventive maintenance programs to protect your investment.',
      link: '/services/maintenance-services'
    },
    {
      icon: FiDollarSign,
      title: 'Financial Reporting',
      description: 'Comprehensive financial statements and tax documentation with real-time access.',
      link: '/services/financial-reporting'
    },
    {
      icon: FiShield,
      title: 'Legal Compliance',
      description: 'Full compliance with Alberta Residential Tenancies Act and municipal regulations.',
      link: '/services/legal-compliance'
    },
    {
      icon: FiStar,
      title: 'Real Estate Investment',
      description: 'Expert investment guidance and portfolio management for property investors.',
      link: '/services/real-estate-investment'
    }
  ];

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Property Management Service Areas",
    "description": "DGM Property Management serves Calgary, Airdrie, and Edmonton with comprehensive property management services.",
    "provider": {
      "@type": "LocalBusiness",
      "name": "DGM Property Management",
      "telephone": "587-800-1215",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Calgary",
        "addressRegion": "Alberta",
        "addressCountry": "CA"
      }
    },
    "areaServed": [
      {
        "@type": "Place",
        "name": "Calgary"
      },
      {
        "@type": "Place",
        "name": "Airdrie"
      },
      {
        "@type": "Place",
        "name": "Edmonton"
      }
    ],
    "serviceType": ["Property Management", "Tenant Placement", "Maintenance Services", "Financial Reporting", "Legal Compliance", "Real Estate Investment"]
  };

  return (
    <>
      <SEOHead
        title="Property Management Service Areas | Calgary, Airdrie & Edmonton"
        description="DGM Property Management serves Calgary, Airdrie, and Edmonton with professional property management services. Specialized expertise in residential and commercial properties across all neighborhoods."
        keywords="property management Calgary, property management Airdrie, property management Edmonton, Calgary rental management, Airdrie property manager, Edmonton property management, Alberta property services"
        canonical="/service-areas"
        ogImage="https://images.unsplash.com/photo-1560518883-ce09059eeffa?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&h=630"
        schemaData={schemaData}
      />
      
      <div className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <section className="pt-32 pb-16 bg-gradient-to-br from-blue-600 to-blue-800 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center"
            >
              <h1 className="text-4xl md:text-6xl font-bold mb-6">
                Our Service Areas
              </h1>
              <p className="text-xl text-blue-100 max-w-3xl mx-auto">
                Professional property management services in Calgary, Airdrie, and Edmonton. 
                Specialized expertise across all neighborhoods in Alberta's major cities.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Service Areas Grid */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Alberta Service Areas</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Comprehensive property management services across Calgary, Airdrie, and Edmonton with local market expertise.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-3 gap-8">
              {areas.map((area, index) => (
                <motion.div
                  key={area.name}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 p-8 border border-gray-100"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                      <SafeIcon icon={FiMapPin} className="w-6 h-6 text-blue-600" />
                    </div>
                    <h3 className="text-2xl font-semibold text-gray-900">{area.name}</h3>
                  </div>
                  
                  <p className="text-gray-600 mb-6">{area.description}</p>
                  
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="bg-gray-50 rounded-lg p-3 text-center">
                      <div className="text-xl font-bold text-blue-600">{area.stats.properties}</div>
                      <div className="text-sm text-gray-600">Properties</div>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3 text-center">
                      <div className="text-xl font-bold text-blue-600">{area.stats.satisfaction}</div>
                      <div className="text-sm text-gray-600">Satisfaction</div>
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Key Areas:</h4>
                    <div className="flex flex-wrap gap-2">
                      {area.neighborhoods.slice(0, 5).map((neighborhood, idx) => (
                        <span 
                          key={idx} 
                          className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded"
                        >
                          {neighborhood}
                        </span>
                      ))}
                      <span className="text-xs bg-gray-100 text-gray-800 px-2 py-1 rounded">
                        +{area.neighborhoods.length - 5} more
                      </span>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Services Overview */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Property Management Services</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Comprehensive solutions for property owners across Alberta. Click on any service to learn more.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {services.map((service, index) => (
                <motion.a
                  key={service.title}
                  href={service.link}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  whileHover={{ y: -5 }}
                  className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 p-8 border border-gray-100 block"
                >
                  <div className="flex items-center gap-4 mb-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                      <SafeIcon icon={service.icon} className="w-6 h-6 text-blue-600" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900">{service.title}</h3>
                  </div>
                  <p className="text-gray-600 mb-4">{service.description}</p>
                  <div className="text-blue-600 font-medium flex items-center gap-1">
                    Learn more
                    <SafeIcon icon={FiCheck} className="w-4 h-4" />
                  </div>
                </motion.a>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-blue-600 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="space-y-6"
            >
              <h2 className="text-4xl font-bold mb-4">Serving Alberta's Major Markets</h2>
              <p className="text-xl text-blue-100 max-w-2xl mx-auto">
                Whether you own property in Calgary, Airdrie, or Edmonton, our local expertise 
                ensures optimal management and maximum returns for your investment.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <motion.a
                  href="/contact"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-4 rounded-lg font-semibold transition-colors duration-200"
                >
                  Get Free Property Analysis
                </motion.a>
                <motion.a
                  href="tel:587-800-1215"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="border-2 border-white hover:bg-white hover:text-blue-600 px-8 py-4 rounded-lg font-semibold transition-colors duration-200"
                >
                  Call 587-800-1215
                </motion.a>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
};

export default ServiceAreasPage;